import { useEffect, useMemo, useRef, useState, type RefObject, type TouchEvent } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { appApi, coachApi } from "@/api";
import ImagePreviewModal from "@/components/feature/ImagePreviewModal";
import { CoachSwitchModal } from "@/components/feature/CoachSwitchModal";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { auth } from "@/lib/auth";
import { locationStore } from "@/lib/location";
import { buildLoginRedirect } from "@/lib/navigation";
import type { CoachDetailGoodsItem, CoachDetailResult, CoachMomentComment, CoachMomentItem } from "@/api/modules/coach";
import type { CoachListItem } from "@/types/api";

const DEFAULT_COACH_ID = 115;
const FALLBACK_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 560">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#F8E3CF"/>
        <stop offset="52%" stop-color="#F3C79E"/>
        <stop offset="100%" stop-color="#D98C52"/>
      </linearGradient>
      <linearGradient id="card" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.35"/>
        <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0.08"/>
      </linearGradient>
    </defs>
    <rect width="400" height="560" fill="url(#bg)"/>
    <circle cx="320" cy="100" r="92" fill="#FFFFFF" fill-opacity="0.16"/>
    <circle cx="92" cy="470" r="132" fill="#B96533" fill-opacity="0.18"/>
    <rect x="44" y="72" width="312" height="416" rx="32" fill="url(#card)" stroke="#FFFFFF" stroke-opacity="0.22"/>
    <circle cx="200" cy="210" r="82" fill="#FFF4EA"/>
    <path d="M136 338c18-34 48-52 64-52s46 18 64 52v38H136z" fill="#FFF4EA"/>
    <text x="200" y="438" text-anchor="middle" font-size="28" font-family="Arial, sans-serif" font-weight="700" fill="#FFFFFF">ATHOME</text>
    <text x="200" y="470" text-anchor="middle" font-size="14" font-family="Arial, sans-serif" letter-spacing="4" fill="#FFF7F0">THERAPIST</text>
  </svg>`,
)}`;

type TabType = "menu" | "reviews" | "moments";
type MediaType = "video" | "image";
type MediaKind = "video" | "life" | "work" | "gallery";

interface DetailMediaItem {
  type: MediaType;
  kind: MediaKind;
  src: string;
  label: string;
}

interface TherapistDetailLocationState {
  previewImage?: string;
  previewName?: string;
}

interface MomentMediaView {
  type: "image" | "video";
  src: string;
  cover?: string;
}

function createGoodsQuantityMap(goodsList?: CoachDetailGoodsItem[] | null) {
  return Object.fromEntries((goodsList || []).map((item) => [item.id, 0])) as Record<number, number>;
}

function getSingleSelectedServiceEntry(goodsList: CoachDetailGoodsItem[] | undefined | null, quantities: Record<number, number>) {
  for (const item of goodsList || []) {
    const qty = quantities[item.id] || 0;
    if (qty > 0) {
      return { goodsId: item.id, qty };
    }
  }
  return null;
}

function CoachMomentCard({
  moment,
  coachName,
  coachAvatar,
  commentValue,
  submitting,
  liking,
  deletingCommentId,
  currentUserId,
  commentsExpanded,
  onToggleLike,
  onToggleComments,
  onDeleteComment,
  onCommentChange,
  onSubmitComment,
  onFocusComment,
}: {
  moment: CoachMomentItem;
  coachName: string;
  coachAvatar: string;
  commentValue: string;
  submitting: boolean;
  liking: boolean;
  deletingCommentId: number | null;
  currentUserId: number | null;
  commentsExpanded: boolean;
  onToggleLike: () => void;
  onToggleComments: () => void;
  onDeleteComment: (commentId: number) => void;
  onCommentChange: (value: string) => void;
  onSubmitComment: () => void;
  onFocusComment: () => void;
}) {
  const media = getMomentMedia(moment);
  const comments = moment.comments || [];
  return (
    <div className="rounded-[22px] bg-white px-4 py-4" style={{ border: "1px solid #F2F2F2", boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)" }}>
      <div className="mb-3 flex items-start gap-3">
        <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full" style={{ background: "#F5F5F5" }}>
          <img src={coachAvatar} alt={coachName} className="h-full w-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold" style={{ color: "#1A1A1A" }}>{coachName}</span>
            <span className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: "#FFF3EC", color: "#FF5C00" }}>技师动态</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs" style={{ color: "#B0B0B0" }}>
            {moment.time ? <span>{moment.time}</span> : null}
            {moment.locationName ? (
              <>
                <span>·</span>
                <i className="ri-map-pin-2-line" style={{ fontSize: 12 }} />
                <span className="max-w-[180px] truncate">{moment.locationName}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      {moment.text ? <p className="mb-3 whitespace-pre-wrap text-sm leading-6" style={{ color: "#333" }}>{moment.text}</p> : null}
      {media ? (
        <div
          className="mb-3 overflow-hidden rounded-2xl"
          style={{ maxWidth: media.type === "video" ? 360 : 300, background: media.type === "video" ? "#000" : "#F7F7F7" }}
        >
          {media.type === "image" ? (
            <img src={media.src} alt="技师动态图片" className="w-full object-cover object-top" style={{ maxHeight: 360, display: "block" }} />
          ) : (
            <video
              src={media.src}
              poster={media.cover || undefined}
              controls
              playsInline
              preload="metadata"
              className="w-full"
              style={{ maxHeight: 460, display: "block", objectFit: "contain", background: "#000" }}
            />
          )}
        </div>
      ) : null}
      <div className="flex items-center gap-5 text-xs" style={{ color: "#A3A3A3" }}>
        <button type="button" onClick={onToggleLike} disabled={liking} className="flex items-center gap-1 cursor-pointer" style={{ color: moment.liked ? "#FF5C00" : "#A3A3A3", background: "transparent", border: "none", padding: 0 }}>
          <i className={moment.liked ? "ri-heart-3-fill" : "ri-heart-3-line"} />{formatMomentCount(moment.likes)}
        </button>
        <button type="button" onClick={onToggleComments} className="flex items-center gap-1 cursor-pointer" style={{ color: commentsExpanded ? "#FF5C00" : "#A3A3A3", background: "transparent", border: "none", padding: 0 }}>
          <i className={commentsExpanded ? "ri-chat-3-fill" : "ri-chat-3-line"} />{formatMomentCount(moment.commentCount ?? comments.length)}
        </button>
      </div>
      {commentsExpanded && comments.length ? (
        <div className="mt-3 space-y-2 rounded-2xl px-3 py-3" style={{ background: "#F7F7F7" }}>
          {comments.map((comment) => (
            <div key={comment.id} className="flex items-start gap-2">
              <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-full" style={{ background: "#EDEDED" }}>
                {comment.avatar ? <img src={resolveImage(comment.avatar)} alt={comment.user || "用户"} className="h-full w-full object-cover" /> : <i className="ri-user-line flex h-full w-full items-center justify-center" style={{ color: "#B8B8B8", fontSize: 13 }} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-xs font-semibold" style={{ color: "#333" }}>{comment.user || "匿名用户"}</span>
                  <span className="text-[11px]" style={{ color: "#BBBBBB" }}>{comment.date || ""}</span>
                  {(comment.canDelete || (currentUserId != null && Number(comment.userId) === currentUserId)) ? (
                    <button type="button" onClick={() => onDeleteComment(comment.id)} disabled={deletingCommentId === comment.id} className="text-[11px]" style={{ color: "#FF5C00", background: "transparent", border: "none", padding: 0 }}>
                      {deletingCommentId === comment.id ? "删除中" : "删除"}
                    </button>
                  ) : null}
                </div>
                <p className="mt-0.5 text-xs leading-5" style={{ color: "#555" }}>{comment.text}</p>
                {comment.replyText ? (
                  <div className="mt-1 rounded-xl px-2 py-1.5" style={{ background: "#FFF7F0", color: "#8A4D24" }}>
                    <span className="text-[11px] font-semibold">技师回复：</span>
                    <span className="text-xs">{comment.replyText}</span>
                    {comment.replyDate ? <span className="ml-1 text-[11px]" style={{ color: "#C7A48E" }}>{comment.replyDate}</span> : null}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {commentsExpanded ? (
        <div className="mt-3 flex items-center gap-2 rounded-full px-3 py-2" style={{ background: "#F8F8F8", border: "1px solid #EEEEEE" }}>
          <input
            value={commentValue}
            onFocus={onFocusComment}
            onChange={(event) => onCommentChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                onSubmitComment();
              }
            }}
            maxLength={200}
            placeholder="说点什么..."
            className="min-w-0 flex-1 bg-transparent text-xs outline-none"
            style={{ color: "#333" }}
          />
          <button type="button" onClick={onSubmitComment} disabled={submitting || !commentValue.trim()} className="text-xs font-semibold" style={{ color: commentValue.trim() && !submitting ? "#FF5C00" : "#C8C8C8" }}>
            {submitting ? "发送中" : "发送"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

interface MockReviewItem {
  id: number;
  user: string;
  avatar?: string;
  date: string;
  rating: number;
  content: string;
  tags: string[];
}

const MOCK_REVIEWS: MockReviewItem[] = [
  {
    id: 1,
    user: "苏喂苏喂",
    avatar:
      "https://readdy.ai/api/search-image?query=cute%20cat%20pet%20profile%20photo%20orange%20tabby%20close%20up&width=80&height=80&seq=rev001&orientation=squarish",
    date: "2025-03-05",
    rating: 5,
    content: "手法非常专业，力度恰到好处，做完整个人都轻松了，强烈推荐！",
    tags: ["专业技术", "力度到位"],
  },
  {
    id: 2,
    user: "匿名用户",
    avatar: "",
    date: "2025-02-20",
    rating: 5,
    content: "技师非常专业，手法到位，做完睡眠好多了，下次还会预约。",
    tags: ["改善睡眠", "服务贴心"],
  },
  {
    id: 3,
    user: "林***",
    avatar:
      "https://readdy.ai/api/search-image?query=young%20asian%20woman%20portrait%20casual%20natural%20light%20soft%20smile&width=80&height=80&seq=rev003&orientation=squarish",
    date: "2025-01-15",
    rating: 5,
    content: "服务很好，很满意，推荐给朋友们了！体验非常棒，下次还会来。",
    tags: ["强烈推荐", "超出期望"],
  },
  {
    id: 4,
    user: "王**",
    avatar: "",
    date: "2024-12-30",
    rating: 4,
    content: "整体挺好的，手法专业，就是时间上稍微等了一会儿，整体还是非常满意的。",
    tags: ["手法专业"],
  },
];

function toNumber(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toPositiveNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function resolveImage(value?: string | null) {
  return resolveCustomerAssetUrl(value) || FALLBACK_IMAGE;
}

function resolveVideo(value?: string | null) {
  return resolveCustomerAssetUrl(value) || "";
}

function formatDistanceText(value?: string | number | null) {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  const distance = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(distance) || distance <= 0) {
    return "附近可上门";
  }
  if (distance < 1) {
    return `${Math.max(1, Math.round(distance * 1000))}m`;
  }
  return `${distance.toFixed(distance >= 100 ? 0 : 1)}km`;
}

function formatDateText(value?: string) {
  if (!value) {
    return "";
  }
  const raw = String(value).trim();
  if (!raw) {
    return "";
  }
  if (raw.includes("T") || raw.includes(" ")) {
    return raw.slice(0, 10);
  }
  return raw;
}

function formatMomentCount(value?: number | string | null) {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) {
    return "0";
  }
  if (count >= 10000) {
    const wan = count / 10000;
    return `${(Number.isInteger(wan) ? String(wan) : wan.toFixed(1)).replace(/\.0$/, "")}万`;
  }
  return String(Math.floor(count));
}

function getMomentMedia(moment: CoachMomentItem): MomentMediaView | null {
  if (moment.hasVideo && moment.videoUrl) {
    return {
      type: "video",
      src: resolveCustomerAssetUrl(moment.videoUrl) || "",
      cover: resolveCustomerAssetUrl(moment.videoCover) || "",
    };
  }
  const firstPhoto = (moment.photos || []).map(resolveCustomerAssetUrl).find(Boolean);
  return firstPhoto ? { type: "image", src: firstPhoto } : null;
}

function formatMoneyText(value?: number | string | null) {
  const amount = Number(value);
  return `¥${Number.isFinite(amount) ? amount : 0}`;
}

function getGoodsMetaItems(item?: CoachDetailGoodsItem | null) {
  if (!item) {
    return [];
  }
  const items = Array.isArray(item.marketing_tags)
    ? item.marketing_tags.map((tag) => String(tag || "").trim()).filter(Boolean)
    : [];
  const displayOrderNum = toNumber(item.display_order_num, 0);
  if (displayOrderNum > 0) {
    items.push(`${displayOrderNum}人选择`);
  }
  return items;
}

function formatGoodsSelectionText(value?: number | string | null) {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) {
    return "";
  }
  if (count >= 10000) {
    const wan = count / 10000;
    const text = Number.isInteger(wan) ? String(wan) : wan.toFixed(1);
    return `${text.replace(/\.0$/, "")}万人选择`;
  }
  return `${count}人选择`;
}

function getGoodsSummaryMetaItems(item?: CoachDetailGoodsItem | null) {
  if (!item) {
    return [];
  }
  const items: string[] = [];
  const duration = toNumber(item.duration, 0);
  if (duration > 0) {
    items.push(`${duration}分钟`);
  }
  const marketingTags = Array.isArray(item.marketing_tags)
    ? item.marketing_tags.map((tag) => String(tag || "").trim()).filter(Boolean)
    : [];
  return [...items, ...marketingTags].slice(0, 3);
}

function getOperationTagStyle(index: number) {
  if (index < 2) {
    return {
      background: "#F97316",
      color: "#FFFFFF",
      border: "1px solid #F97316",
      boxShadow: "0 2px 8px rgba(249, 115, 22, 0.14)",
    };
  }

  return {
    background: "#FFF7F2",
    color: "#E06A1A",
    border: "1px solid #F7D6BF",
    boxShadow: "none",
  };
}

function buildMediaList(detail: CoachDetailResult | null): DetailMediaItem[] {
  if (!detail) {
    return [];
  }

  const items: DetailMediaItem[] = [];
  const seen = new Set<string>();

  const pushImage = (value: string | undefined | null, kind: MediaKind, label: string) => {
    const src = resolveCustomerAssetUrl(value);
    if (!src || seen.has(src)) {
      return;
    }
    seen.add(src);
    items.push({ type: "image", kind, src, label });
  };

  const introVideo = resolveVideo(detail.intro_video);
  if (introVideo) {
    seen.add(introVideo);
    items.push({ type: "video", kind: "video", src: introVideo, label: "视频" });
  }

  const lifePhotos = Array.isArray(detail.life_photo) ? detail.life_photo : [];
  if (lifePhotos.length > 0) {
    for (const photo of lifePhotos) {
      pushImage(photo, "life", "生活照");
    }
  } else {
    pushImage(detail.work_photo, "work", "工作照");
  }

  const galleryPhotos = Array.isArray(detail.photos) ? detail.photos : [];
  for (const photo of galleryPhotos) {
    pushImage(photo, "gallery", "工作照");
  }

  if (items.length === 0) {
    pushImage(detail.work_photo, "work", "工作照");
  }

  return items.length > 0
    ? items
    : [{ type: "image", kind: "work", src: FALLBACK_IMAGE, label: "工作照" }];
}

function ServicePickerModal({
  goodsList,
  quantities,
  onIncrease,
  onDecrease,
  onClose,
  onSubmit,
}: {
  goodsList: CoachDetailGoodsItem[];
  quantities: Record<number, number>;
  onIncrease: (goodsId: number) => void;
  onDecrease: (goodsId: number) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const selectedEntry = getSingleSelectedServiceEntry(goodsList, quantities);
  const total = goodsList.reduce((sum, item) => {
    const quantity = selectedEntry?.goodsId === item.id ? selectedEntry.qty : 0;
    return sum + toNumber(item.price, 0) * quantity;
  }, 0);
  const hasAny = Boolean(selectedEntry);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center" style={{ background: "rgba(0,0,0,0.45)" }} onClick={onClose}>
      <div
        className="w-full bg-white rounded-t-3xl"
        style={{
          maxWidth: 480,
          maxHeight: "80vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          animation: "slideUp 0.25s ease",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-shrink-0" style={{ borderBottom: "1px solid #F5F5F5" }}>
          <span className="text-base font-bold text-[#1A1A1A]">请选择服务项目</span>
          <button type="button" onClick={onClose} className="cursor-pointer w-8 h-8 flex items-center justify-center">
            <i className="ri-close-line text-xl" style={{ color: "#999" }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {goodsList.map((item) => {
            const quantity = selectedEntry?.goodsId === item.id ? selectedEntry.qty : 0;
            const metaItems = getGoodsSummaryMetaItems(item);
            const selectionText = formatGoodsSelectionText(item.display_order_num);
            const displayMeta = selectionText ? [...metaItems, selectionText] : metaItems;
            return (
              <div key={item.id} className="flex items-center gap-3">
                <div className="h-16 w-20 flex-shrink-0 overflow-hidden rounded-xl">
                  <img src={resolveImage(item.image)} alt={item.name || "服务项目"} className="h-full w-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="mb-0.5 line-clamp-2 text-sm font-semibold leading-snug text-[#1A1A1A]">{item.name || "服务项目"}</p>
                  <p className="mb-1 text-xs text-[#AAAAAA]">{metaItems.join(" · ")}</p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold text-[#FF5C00]">{formatMoneyText(item.price)}会员价</span>
                    {item.scribing_price ? <span className="text-xs text-[#CCCCCC] line-through">{formatMoneyText(item.scribing_price)}</span> : null}
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {quantity > 0 ? (
                    <>
                      <button type="button" onClick={() => onDecrease(item.id)} className="flex h-6 w-6 items-center justify-center rounded-full" style={{ border: "1.5px solid #DDDDDD", color: "#888" }}>
                        <i className="ri-subtract-line text-xs" />
                      </button>
                      <span className="w-4 text-center text-sm font-bold text-[#1A1A1A]">{quantity}</span>
                    </>
                  ) : null}
                  <button type="button" onClick={() => onIncrease(item.id)} className="flex h-6 w-6 items-center justify-center rounded-full" style={{ background: "#FF5C00", color: "#fff" }}>
                    <i className="ri-add-line text-xs" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-4 py-3 flex items-center justify-between flex-shrink-0" style={{ borderTop: "1px solid #F5F5F5" }}>
          <span className="text-sm font-bold" style={{ color: hasAny ? "#FF5C00" : "#AAAAAA" }}>
            合计：{hasAny ? formatMoneyText(total) : "¥0"}
          </span>
          <button type="button" onClick={onSubmit} disabled={!hasAny} className="px-8 py-3 rounded-full text-white font-bold text-sm disabled:opacity-50" style={{ background: hasAny ? "linear-gradient(90deg, #FF8C3A, #FF5C00)" : "#E0E0E0" }}>
            提交订单
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DetailLoadingShell({
  previewImage,
  previewName,
  fadingOut,
  onBack,
}: {
  previewImage: string;
  previewName?: string;
  fadingOut: boolean;
  onBack: () => void;
}) {
  return (
    <div
      className="absolute inset-0 z-20 overflow-hidden bg-[#F4F4F6]"
      style={{
        opacity: fadingOut ? 0 : 1,
        transform: fadingOut ? "scale(1.01)" : "scale(1)",
        transition: "opacity 280ms ease, transform 320ms ease",
        pointerEvents: fadingOut ? "none" : "auto",
      }}
    >
      <div className="relative h-[340px] overflow-hidden bg-[#E9E4DE]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("${previewImage || FALLBACK_IMAGE}")`,
            backgroundSize: "contain",
            backgroundPosition: "center top",
            transform: "scale(1.03)",
            filter: "saturate(0.92)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(16,18,22,0.18) 0%, rgba(16,18,22,0.08) 24%, rgba(255,255,255,0) 58%, rgba(244,244,246,0.92) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, rgba(255,255,255,0) 24%, rgba(255,255,255,0.18) 42%, rgba(255,255,255,0) 60%)",
            backgroundSize: "220% 100%",
            animation: "detailShellSweep 1.8s ease-in-out infinite",
          }}
        />
        <button
          type="button"
          onClick={onBack}
          className="absolute left-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/35"
        >
          <i className="ri-arrow-left-line text-[22px] text-white" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-5 pt-14">
          <div className="mb-3 h-7 rounded-full bg-white/30" style={{ width: previewName ? Math.min(Math.max(previewName.length * 18, 96), 180) : 116 }} />
          <div className="mb-2 h-4 w-40 rounded-full bg-white/20" />
          <div className="flex gap-2">
            <div className="h-7 w-20 rounded-full bg-white/22" />
            <div className="h-7 w-24 rounded-full bg-white/18" />
            <div className="h-7 w-20 rounded-full bg-white/16" />
          </div>
        </div>
      </div>

      <div className="space-y-3 px-4 py-4">
        <div className="rounded-[24px] bg-white px-4 py-4 shadow-[0_8px_26px_rgba(15,23,42,0.04)]">
          <div className="mb-3 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[#F5F5F7]" />
            <div className="h-4 w-28 rounded-full bg-[#F1F2F4]" />
            <div className="ml-auto h-4 w-20 rounded-full bg-[#F1F2F4]" />
          </div>
          <div className="mb-2 h-4 w-full rounded-full bg-[#F5F5F7]" />
          <div className="h-4 w-3/4 rounded-full bg-[#F5F5F7]" />
        </div>

        <div className="rounded-[24px] bg-white px-4 py-4 shadow-[0_8px_26px_rgba(15,23,42,0.04)]">
          <div className="mb-4 flex gap-5">
            <div className="h-5 w-20 rounded-full bg-[#F1F2F4]" />
            <div className="h-5 w-20 rounded-full bg-[#F7F7F8]" />
          </div>
          {[0, 1, 2].map((item) => (
            <div key={item} className="flex items-center gap-3 py-3">
              <div className="h-16 w-20 rounded-xl bg-[#F3F4F6]" />
              <div className="min-w-0 flex-1">
                <div className="mb-2 h-4 w-4/5 rounded-full bg-[#F1F2F4]" />
                <div className="mb-3 h-3 w-2/3 rounded-full bg-[#F6F7F8]" />
                <div className="h-4 w-28 rounded-full bg-[#FFF1E7]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HeroMediaCarousel({
  detail,
  mediaList,
  activeIndex,
  videoRef,
  videoPoster,
  videoPlaying,
  videoStarted,
  videoReady,
  onBack,
  onToggleVideo,
  onChangeMedia,
  onOpenImagePreview,
  onTouchStart,
  onTouchEnd,
  onVideoLoadedData,
  onVideoPlay,
  onVideoPause,
}: {
  detail: CoachDetailResult | null;
  mediaList: DetailMediaItem[];
  activeIndex: number;
  videoRef: RefObject<HTMLVideoElement | null>;
  videoPoster: string;
  videoPlaying: boolean;
  videoStarted: boolean;
  videoReady: boolean;
  onBack: () => void;
  onToggleVideo: () => void | Promise<void>;
  onChangeMedia: (index: number) => void;
  onOpenImagePreview: () => void;
  onTouchStart: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: (event: TouchEvent<HTMLDivElement>) => void;
  onVideoLoadedData: () => void;
  onVideoPlay: () => void;
  onVideoPause: () => void;
}) {
  const current = mediaList[activeIndex] ?? mediaList[0];
  const displayRating = toNumber(detail?.display_rating ?? detail?.score_comment, 5);
  const displayOrderNum = toNumber(detail?.display_order_num ?? detail?.order_num, 0);
  const isVideo = current?.type === "video";
  const heroBackgroundImage = current?.type === "video" ? videoPoster : current?.src || FALLBACK_IMAGE;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: isVideo ? "min(100vw, 480px)" : 340,
        flexShrink: 0,
        backgroundColor: "#111",
        backgroundImage: `url("${heroBackgroundImage}")`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {current?.type === "video" ? (
        <>
          <img src={videoPoster} alt={detail?.name || "技师视频封面"} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", display: "block" }} />
          {videoStarted ? (
            <video ref={videoRef} src={current.src} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", display: "block", opacity: videoReady ? 1 : 0, transition: "opacity 180ms ease" }} playsInline loop controls={false} poster={videoPoster} onClick={() => void onToggleVideo()} onLoadedData={onVideoLoadedData} onPlay={onVideoPlay} onPause={onVideoPause} />
          ) : null}
          {!videoPlaying && (
            <button type="button" onClick={() => void onToggleVideo()} style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)", border: "none", cursor: "pointer", zIndex: 5 }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="ri-play-fill" style={{ color: "#FF5C00", fontSize: 26, marginLeft: 3 }} />
              </div>
            </button>
          )}
          {videoPlaying && (
            <button type="button" onClick={() => void onToggleVideo()} style={{ position: "absolute", bottom: 60, right: 16, zIndex: 10, width: 36, height: 36, borderRadius: "50%", background: "rgba(0,0,0,0.5)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <i className="ri-pause-fill" style={{ color: "#fff", fontSize: 18 }} />
            </button>
          )}
        </>
      ) : (
        <button
          type="button"
          onClick={onOpenImagePreview}
          style={{ position: "absolute", inset: 0, border: "none", padding: 0, background: "transparent", cursor: "pointer" }}
        >
          <img src={current?.src || FALLBACK_IMAGE} alt={detail?.name || "技师"} style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", display: "block" }} />
        </button>
      )}

      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 45%)", pointerEvents: "none" }} />
      <button type="button" onClick={onBack} style={{ position: "absolute", top: 16, left: 16, zIndex: 20, width: 40, height: 40, borderRadius: "50%", background: "rgba(0,0,0,0.4)", border: "1.5px solid rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <i className="ri-arrow-left-line" style={{ color: "#fff", fontSize: 22 }} />
      </button>

      {current?.label && (
        <div style={{ position: "absolute", top: 16, right: 16, zIndex: 20, background: "rgba(0,0,0,0.45)", borderRadius: 20, padding: "3px 10px", display: "flex", alignItems: "center", gap: 4 }}>
          {current.type === "video" && <i className="ri-video-line" style={{ color: "#fff", fontSize: 12 }} />}
          {current.type === "image" && current.kind === "life" && <i className="ri-image-line" style={{ color: "#fff", fontSize: 12 }} />}
          {current.type === "image" && current.kind !== "life" && <i className="ri-user-3-line" style={{ color: "#fff", fontSize: 12 }} />}
          <span style={{ color: "#fff", fontSize: 11 }}>{current.label}</span>
        </div>
      )}

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "40px 16px 14px", background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)", pointerEvents: "none" }}>
        <div className="mb-2 flex items-end justify-between">
          <div>
            <span style={{ color: "#fff", fontSize: 20, fontWeight: 700, display: "block", marginBottom: 4 }}>{detail?.name || "技师"}</span>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((i) => <i key={i} className={i <= Math.floor(displayRating) ? "ri-star-fill" : "ri-star-line"} style={{ color: "#FFD700", fontSize: 13 }} />)}
              <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 13, fontWeight: 600, marginLeft: 2 }}>{displayRating.toFixed(1)}</span>
              <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>· {displayOrderNum}单</span>
            </div>
          </div>
          <span style={{ background: detail?.work_status === 1 ? "#E8FBF3" : "#FFF3EC", color: detail?.work_status === 1 ? "#17B978" : "#FF7B22", fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 20, pointerEvents: "auto" }}>
            {detail?.work_status === 1 ? "可预约" : "休息中"}
          </span>
        </div>

        {mediaList.length > 1 && (
          <div className="flex items-center gap-1.5" style={{ pointerEvents: "auto" }}>
            {mediaList.map((item, index) => (
              <button key={`${item.kind}-${item.src}-${index}`} type="button" onClick={() => onChangeMedia(index)} style={{ width: activeIndex === index ? 18 : 6, height: 6, borderRadius: 99, background: activeIndex === index ? "#FF5C00" : "rgba(255,255,255,0.5)", border: "none", cursor: "pointer", transition: "width 0.2s ease", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {item.type === "video" && activeIndex === index && <i className="ri-video-fill" style={{ color: "#fff", fontSize: 7 }} />}
              </button>
            ))}
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 10, marginLeft: 4 }}>{activeIndex + 1}/{mediaList.length}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TherapistDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const coachId = Number(searchParams.get("id") || DEFAULT_COACH_ID);
  const autoShow = searchParams.get("showService") === "true";
  const locationState = (location.state as TherapistDetailLocationState | null) ?? null;

  const [activeTab, setActiveTab] = useState<TabType>("menu");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<CoachDetailResult | null>(null);
  const [moments, setMoments] = useState<CoachMomentItem[]>([]);
  const [momentCommentInputs, setMomentCommentInputs] = useState<Record<number, string>>({});
  const [expandedMomentComments, setExpandedMomentComments] = useState<Record<number, boolean>>({});
  const [submittingMomentCommentId, setSubmittingMomentCommentId] = useState<number | null>(null);
  const [submittingMomentLikeId, setSubmittingMomentLikeId] = useState<number | null>(null);
  const [deletingMomentCommentId, setDeletingMomentCommentId] = useState<number | null>(null);
  const [deleteMomentCommentConfirm, setDeleteMomentCommentConfirm] = useState<{ momentId: number; commentId: number } | null>(null);
  const [collected, setCollected] = useState(false);
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [showCoachSwitchModal, setShowCoachSwitchModal] = useState(false);
  const [coachSwitchLoading, setCoachSwitchLoading] = useState(false);
  const [switchableCoaches, setSwitchableCoaches] = useState<CoachListItem[]>([]);
  const [serviceQuantities, setServiceQuantities] = useState<Record<number, number>>({});
  const [currentLocation, setCurrentLocation] = useState(() => locationStore.getFresh());
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [showLoadingShell, setShowLoadingShell] = useState(true);
  const [shellFadingOut, setShellFadingOut] = useState(false);
  const [imagePreview, setImagePreview] = useState<{ title: string; images: string[]; initialIndex: number } | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const heroSwipeTriggeredRef = useRef(false);

  useEffect(() => {
    setCurrentLocation(locationStore.getFresh());
    return locationStore.subscribe(() => setCurrentLocation(locationStore.getFresh()));
  }, []);

  useEffect(() => {
    setDetailVisible(false);
    setShowLoadingShell(true);
    setShellFadingOut(false);
  }, [coachId]);

  useEffect(() => {
    let cancelled = false;

    const loadDetail = async () => {
      setLoading(true);
      setError("");

      try {
        const result = await coachApi.getCoachDetail(coachId, {
          longitude: currentLocation?.longitude,
          latitude: currentLocation?.latitude,
        });

        if (!cancelled) {
          setDetail(result);
          setCollected(result.is_collect === 1);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : "技师详情加载失败");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadDetail();

    return () => {
      cancelled = true;
    };
  }, [coachId, currentLocation?.latitude, currentLocation?.longitude]);

  useEffect(() => {
    let cancelled = false;
    const loadMoments = async () => {
      try {
        const result = await coachApi.getCoachMoments(coachId, 20);
        if (!cancelled) {
          setMoments(Array.isArray(result?.lists) ? result.lists : []);
        }
      } catch {
        if (!cancelled) {
          setMoments([]);
        }
      }
    };
    void loadMoments();
    return () => {
      cancelled = true;
    };
  }, [coachId]);

  const mediaList = useMemo(() => buildMediaList(detail), [detail]);
  const previewableImages = useMemo(
    () => mediaList.filter((item): item is DetailMediaItem & { type: "image" } => item.type === "image"),
    [mediaList],
  );
  useEffect(() => {
    setActiveMediaIndex(0);
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setVideoPlaying(false);
    setVideoStarted(false);
    setVideoReady(false);
  }, [mediaList]);

  useEffect(() => {
    setServiceQuantities(createGoodsQuantityMap(detail?.goods_lists));
  }, [detail]);

  useEffect(() => {
    if (autoShow && detail?.goods_lists?.length) {
      setActiveTab("menu");
      setServiceQuantities(createGoodsQuantityMap(detail.goods_lists));
      setShowServicePicker(true);
    }
  }, [autoShow, detail?.goods_lists]);

  const hasMoments = moments.length > 0;

  useEffect(() => {
    if (activeTab === "moments" && !hasMoments) {
      setActiveTab("menu");
    }
  }, [activeTab, hasMoments]);

  const currentMedia = mediaList[activeMediaIndex] ?? mediaList[0];
  const previewImage = resolveCustomerAssetUrl(locationState?.previewImage) || "";
  const previewName = String(locationState?.previewName || "").trim();
  const videoPoster = resolveCustomerAssetUrl(detail?.work_photo) || FALLBACK_IMAGE;
  const displayRating = toNumber(detail?.display_rating ?? detail?.score_comment, 5);
  const reviewComments = detail?.comment_lists || [];
  const reviewSummaryItems = Array.isArray(detail?.review_summary?.items) ? detail?.review_summary?.items || [] : [];
  const reviewRating = toNumber(detail?.review_summary?.score ?? detail?.display_rating ?? detail?.score_comment, 5);
  const commentCount = toNumber(detail?.comment_num, reviewComments.length);
  const realNameVerified = Boolean(String(detail?.id_card || "").trim());
  const operationTags = (detail?.operation_tags || []).map((item) => String(item || "").trim()).filter(Boolean);
  const currentUserId = toPositiveNumber(auth.getUserInfo()?.id);
  const updateMomentCommentInput = (momentId: number, value: string) => {
    setMomentCommentInputs((prev) => ({ ...prev, [momentId]: value }));
  };
  const toggleMomentComments = (momentId: number) => {
    setExpandedMomentComments((prev) => ({ ...prev, [momentId]: !prev[momentId] }));
  };
  const focusMomentComment = () => {
    if (!auth.getToken()) {
      navigate(buildLoginRedirect(`/therapist-detail?id=${coachId}`));
    }
  };
  const submitMomentComment = async (momentId: number) => {
    const content = String(momentCommentInputs[momentId] || "").trim();
    if (!content || submittingMomentCommentId) {
      return;
    }
    if (!auth.getToken()) {
      navigate(buildLoginRedirect(`/therapist-detail?id=${coachId}`));
      return;
    }
    setSubmittingMomentCommentId(momentId);
    try {
      const comment = await coachApi.commentCoachMoment(momentId, content);
      setMoments((prev) => prev.map((moment) => {
        if (moment.id !== momentId) {
          return moment;
        }
        const comments: CoachMomentComment[] = [...(moment.comments || []), comment];
        return { ...moment, comments, commentCount: comments.length };
      }));
      setMomentCommentInputs((prev) => ({ ...prev, [momentId]: "" }));
      setExpandedMomentComments((prev) => ({ ...prev, [momentId]: true }));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "评论失败，请稍后重试");
    } finally {
      setSubmittingMomentCommentId(null);
    }
  };
  const toggleMomentLike = async (momentId: number) => {
    if (submittingMomentLikeId) {
      return;
    }
    if (!auth.getToken()) {
      navigate(buildLoginRedirect(`/therapist-detail?id=${coachId}`));
      return;
    }
    setSubmittingMomentLikeId(momentId);
    try {
      const result = await coachApi.toggleCoachMomentLike(momentId);
      setMoments((prev) => prev.map((moment) => (moment.id === momentId ? { ...moment, liked: result.liked, likes: result.likes } : moment)));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "点赞失败，请稍后重试");
    } finally {
      setSubmittingMomentLikeId(null);
    }
  };

  const requestDeleteMomentComment = (momentId: number, commentId: number) => {
    if (!auth.getToken()) {
      navigate(buildLoginRedirect(`/therapist-detail?id=${coachId}`));
      return;
    }
    setDeleteMomentCommentConfirm({ momentId, commentId });
  };

  const deleteMomentComment = async (momentId: number, commentId: number) => {
    if (deletingMomentCommentId) {
      return;
    }
    setDeletingMomentCommentId(commentId);
    try {
      await coachApi.deleteCoachMomentComment(commentId);
      setMoments((prev) => prev.map((moment) => {
        if (moment.id !== momentId) {
          return moment;
        }
        const comments = (moment.comments || []).filter((comment) => comment.id !== commentId);
        return { ...moment, comments, commentCount: comments.length };
      }));
      setDeleteMomentCommentConfirm(null);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "删除失败，请稍后重试");
    } finally {
      setDeletingMomentCommentId(null);
    }
  };
  const reviewBreakdown = [
    ...(reviewSummaryItems.slice(0, 3).map((item) => ({
      label: String(item?.label || "").trim(),
      value: Math.max(0, Math.min(100, toNumber(item?.value, 0))),
    })).filter((item) => item.label) as Array<{ label: string; value: number }>),
    { label: "服务态度", value: 98 },
    { label: "手法专业", value: 96 },
    { label: "准时守约", value: 95 },
  ].slice(0, 3);

  const handleCollectToggle = async () => {
    if (!auth.getToken()) {
      navigate(buildLoginRedirect(`/therapist-detail?id=${coachId}`));
      return;
    }

    try {
      await appApi.toggleCollect({ id: coachId, type: 1 });
      setCollected((prev) => !prev);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "收藏失败");
    }
  };

  const switchMedia = (index: number) => {
    if (mediaList.length === 0) {
      return;
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setVideoPlaying(false);
    setVideoStarted(false);
    setVideoReady(false);
    setActiveMediaIndex((index + mediaList.length) % mediaList.length);
  };

  const handleHeroTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.changedTouches[0];
    heroSwipeTriggeredRef.current = false;
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const handleHeroTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (mediaList.length <= 1 || touchStartXRef.current == null || touchStartYRef.current == null) {
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;

    touchStartXRef.current = null;
    touchStartYRef.current = null;

    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    heroSwipeTriggeredRef.current = true;

    if (deltaX < 0) {
      switchMedia(activeMediaIndex + 1);
      return;
    }

    switchMedia(activeMediaIndex - 1);
  };

  const openCurrentImagePreview = () => {
    if (heroSwipeTriggeredRef.current) {
      heroSwipeTriggeredRef.current = false;
      return;
    }
    if (currentMedia?.type !== "image" || previewableImages.length === 0) {
      return;
    }

    const initialIndex = previewableImages.findIndex((item) => item.src === currentMedia.src);
    setImagePreview({
      title: detail?.name || "",
      images: previewableImages.map((item) => item.src),
      initialIndex: initialIndex >= 0 ? initialIndex : 0,
    });
  };

  const toggleHeroVideo = async () => {
    if (currentMedia?.type !== "video") {
      return;
    }

    if (!videoStarted) {
      setVideoStarted(true);
      setVideoReady(false);
      return;
    }

    if (!videoRef.current) {
      return;
    }

    try {
      if (videoPlaying) {
        videoRef.current.pause();
        setVideoPlaying(false);
      } else {
        await videoRef.current.play();
        setVideoPlaying(true);
      }
    } catch {
      setError("视频暂时无法播放");
    }
  };

  const openServicePicker = (goodsId?: number) => {
    if (detail?.work_status !== 1) {
      setShowServicePicker(false);
      void loadSwitchableCoaches();
      return;
    }
    const nextQuantities = createGoodsQuantityMap(detail?.goods_lists);
    if (goodsId) {
      nextQuantities[goodsId] = 1;
    }
    setServiceQuantities(nextQuantities);
    setShowServicePicker(true);
  };

  const increaseServiceQty = (goodsId: number) => {
    setServiceQuantities((prev) => {
      const next = createGoodsQuantityMap(detail?.goods_lists);
      next[goodsId] = (prev[goodsId] || 0) + 1;
      return next;
    });
  };

  const decreaseServiceQty = (goodsId: number) => {
    setServiceQuantities((prev) => {
      const next = createGoodsQuantityMap(detail?.goods_lists);
      next[goodsId] = Math.max(0, (prev[goodsId] || 0) - 1);
      return next;
    });
  };

  const goToOrderConfirm = (goods: CoachDetailGoodsItem, qty = 1) => {
    if (detail?.work_status !== 1) {
      void loadSwitchableCoaches();
      return;
    }
    if (!auth.getToken()) {
      navigate(buildLoginRedirect(`/order-confirm?goodsId=${goods.id}&coachId=${coachId}&qty=${qty}`));
      return;
    }
    navigate(`/order-confirm?goodsId=${goods.id}&coachId=${coachId}&qty=${qty}`);
  };

  const loadSwitchableCoaches = async () => {
    setShowCoachSwitchModal(true);
    setCoachSwitchLoading(true);
    try {
      const result = await coachApi.getCoachList({
        pageNum: 1,
        pageSize: 6,
        longitude: currentLocation?.longitude,
        latitude: currentLocation?.latitude,
        sortBy: "distance_asc",
      });
      const availableCoaches = (result.records || []).filter(
        (item) => item.id !== coachId && Number(item.workStatus || 0) === 1,
      );
      setSwitchableCoaches(availableCoaches);
    } catch (requestError) {
      setSwitchableCoaches([]);
      setError(requestError instanceof Error ? requestError.message : "可服务技师加载失败");
    } finally {
      setCoachSwitchLoading(false);
    }
  };

  const handleSwitchCoach = (coach: CoachListItem) => {
    setShowCoachSwitchModal(false);
    setShowServicePicker(false);
    navigate(`/therapist-detail?id=${coach.id}`, {
      state: {
        previewImage: coach.workPhoto,
        previewName: coach.name,
      },
    });
  };

  const submitSelectedGoods = () => {
    const selectedEntry = getSingleSelectedServiceEntry(detail?.goods_lists, serviceQuantities);
    if (!selectedEntry) {
      return;
    }

    const { goodsId, qty } = selectedEntry;
    const selectedGoods = detail?.goods_lists?.find((item) => item.id === goodsId);
    if (!selectedGoods) {
      return;
    }

    setShowServicePicker(false);
    goToOrderConfirm(selectedGoods, qty);
  };

  useEffect(() => {
    if (loading || !detail) {
      return;
    }

    let frameId = 0;
    let removeShellTimerId = 0;
    const timerId = window.setTimeout(() => {
      frameId = window.requestAnimationFrame(() => {
        setShellFadingOut(true);
        setDetailVisible(true);
      });
      removeShellTimerId = window.setTimeout(() => {
        setShowLoadingShell(false);
      }, 260);
    }, 24);
    return () => {
      window.clearTimeout(timerId);
      window.clearTimeout(removeShellTimerId);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [loading, detail]);

  useEffect(() => {
    if (!videoStarted || currentMedia?.type !== "video" || !videoRef.current) {
      return;
    }

    let cancelled = false;

    const startPlayback = async () => {
      try {
        await videoRef.current?.play();
        if (!cancelled) {
          setVideoPlaying(true);
        }
      } catch {
        if (!cancelled) {
          setError("视频暂时无法播放");
          setVideoPlaying(false);
        }
      }
    };

    void startPlayback();

    return () => {
      cancelled = true;
    };
  }, [currentMedia, videoStarted]);

  if (!loading && !detail) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#F4F4F6]">
        <p className="mb-4 text-sm text-[#AAAAAA]">技师信息不存在</p>
        <button type="button" onClick={() => navigate("/")} className="rounded-full px-6 py-2 text-sm font-bold text-white" style={{ background: "linear-gradient(90deg, #FF8C3A, #FF5C00)" }}>
          返回首页
        </button>
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-screen flex-col overflow-hidden"
      style={{
        background: "#F4F4F6",
        maxWidth: 480,
        margin: "0 auto",
        opacity: detail ? (detailVisible ? 1 : 0.92) : 1,
        transform: detail ? (detailVisible ? "translateY(0)" : "translateY(8px)") : "translateY(0)",
        transition: "opacity 220ms ease, transform 280ms ease",
      }}
    >
      <style>{`
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes detailShellSweep { 0% { transform: translateX(-42%); opacity: 0; } 18% { opacity: 1; } 100% { transform: translateX(42%); opacity: 0; } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {detail ? (
        <>
      <HeroMediaCarousel
        detail={detail}
        mediaList={mediaList}
        activeIndex={activeMediaIndex}
        videoRef={videoRef}
        videoPoster={videoPoster}
        videoPlaying={videoPlaying}
        videoStarted={videoStarted}
        videoReady={videoReady}
        onBack={() => navigate(-1)}
        onToggleVideo={toggleHeroVideo}
        onChangeMedia={switchMedia}
        onOpenImagePreview={openCurrentImagePreview}
        onTouchStart={handleHeroTouchStart}
        onTouchEnd={handleHeroTouchEnd}
        onVideoLoadedData={() => setVideoReady(true)}
        onVideoPlay={() => setVideoPlaying(true)}
        onVideoPause={() => setVideoPlaying(false)}
      />

      <div className="hide-scrollbar flex-1 overflow-y-auto pb-28">
        {error ? <div className="mx-4 mt-4 rounded-2xl border border-[#FFD8BF] bg-[#FFF4EC] px-4 py-3 text-sm text-[#D94A00]">{error}</div> : null}

        <div className="bg-white px-5 pb-5 pt-5" style={{ borderBottom: "6px solid #F4F4F6" }}>
          <div className="mb-4 flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: "#FF5C00" }}>
                <i className="ri-time-line" style={{ color: "#fff", fontSize: 14 }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: "#AAAAAA", lineHeight: 1.2 }}>最早可约</p>
                <p className="text-sm font-bold" style={{ color: "#1A1A1A", lineHeight: 1.3 }}>{detail?.first_appoint || "尽快上门"}</p>
              </div>
            </div>
            <div style={{ width: 1, height: 28, background: "#EEEEEE" }} />
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: "#F5F5F5" }}>
                <i className="ri-map-pin-2-line" style={{ color: "#888", fontSize: 14 }} />
              </div>
              <div>
                <p className="text-xs" style={{ color: "#AAAAAA", lineHeight: 1.2 }}>距您</p>
                <p className="text-sm font-bold" style={{ color: "#1A1A1A", lineHeight: 1.3 }}>{formatDistanceText(detail?.distance_desc ?? detail?.distance)}</p>
              </div>
            </div>
          </div>

          <p className="mb-4 text-sm leading-6" style={{ color: "#444" }}>{detail?.introduction || "平台认证技师，支持上门服务。"}</p>

          {operationTags.length ? (
            <div className="mb-4 flex flex-wrap gap-2.5">
              {operationTags.map((tag, index) => (
                <span
                  key={`${tag}-${index}`}
                  className="inline-flex items-center rounded-[8px] px-3 py-[7px] text-[13px] font-medium leading-none"
                  style={getOperationTagStyle(index)}
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : null}

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <i className="ri-shield-check-fill" style={{ color: realNameVerified ? "#17B978" : "#AAAAAA", fontSize: 13 }} />
              <span className="text-xs" style={{ color: realNameVerified ? "#17B978" : "#888" }}>{realNameVerified ? "已实名" : "实名认证"}</span>
            </div>
            <div className="flex items-center gap-1">
              <i className="ri-secure-payment-line" style={{ color: "#AAAAAA", fontSize: 13 }} />
              <span className="text-xs" style={{ color: "#888" }}>未服务免费退</span>
            </div>
            <div className="flex items-center gap-1">
              <i className="ri-award-fill" style={{ color: "#FF5C00", fontSize: 13 }} />
              <span className="text-xs" style={{ color: "#FF5C00" }}>技能认证</span>
            </div>
          </div>
        </div>

        <div className="bg-white mt-2">
          <div className="flex" style={{ borderBottom: "1px solid #F2F2F2" }}>
            <button type="button" onClick={() => setActiveTab("menu")} className="flex-1 py-3.5 text-sm font-semibold cursor-pointer relative whitespace-nowrap" style={{ color: activeTab === "menu" ? "#FF5C00" : "#888", background: "transparent", border: "none" }}>
              项目菜单
              {activeTab === "menu" && <span style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)", width: 32, height: 2.5, background: "#FF5C00", borderRadius: 99 }} />}
            </button>
            <button type="button" onClick={() => setActiveTab("reviews")} className="flex-1 py-3.5 text-sm font-semibold cursor-pointer relative whitespace-nowrap" style={{ color: activeTab === "reviews" ? "#FF5C00" : "#888", background: "transparent", border: "none" }}>
              客户评价
              <span className="ml-1 text-xs" style={{ color: activeTab === "reviews" ? "#FF5C00" : "#BBBBBB" }}>({commentCount})</span>
              {activeTab === "reviews" && <span style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)", width: 32, height: 2.5, background: "#FF5C00", borderRadius: 99 }} />}
            </button>
            {hasMoments ? (
              <button type="button" onClick={() => setActiveTab("moments")} className="flex-1 py-3.5 text-sm font-semibold cursor-pointer relative whitespace-nowrap" style={{ color: activeTab === "moments" ? "#FF5C00" : "#888", background: "transparent", border: "none" }}>
                技师动态
                <span className="ml-1 text-xs" style={{ color: activeTab === "moments" ? "#FF5C00" : "#BBBBBB" }}>({moments.length})</span>
                {activeTab === "moments" && <span style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)", width: 32, height: 2.5, background: "#FF5C00", borderRadius: 99 }} />}
              </button>
            ) : null}
          </div>

          {activeTab === "menu" && (
            <div className="px-4 py-2">
              {detail?.goods_lists?.length ? (
                detail.goods_lists.map((item, index) => (
                  <button key={item.id} type="button" onClick={() => openServicePicker(item.id)} className="w-full flex items-center gap-3 py-3 cursor-pointer text-left" style={{ background: "transparent", border: "none", borderBottom: index < detail.goods_lists.length - 1 ? "1px solid #F5F5F5" : "none" }}>
                    <div className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={resolveImage(item.image)} alt={item.name || "服务项目"} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0 self-stretch flex flex-col justify-between">
                      <p className="text-sm font-semibold leading-snug mb-1 line-clamp-2" style={{ color: "#1A1A1A" }}>{item.name || "服务项目"}</p>
                      <p className="text-xs mb-2 line-clamp-1" style={{ color: "#AAAAAA" }}>{getGoodsSummaryMetaItems(item).join(" · ")}</p>
                      <div className="flex items-end justify-between gap-3">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-sm font-bold" style={{ color: "#FF5C00" }}>{formatMoneyText(item.price)}</span>
                          {item.scribing_price ? <span className="text-xs line-through" style={{ color: "#CCCCCC" }}>{formatMoneyText(item.scribing_price)}</span> : null}
                        </div>
                        {formatGoodsSelectionText(item.display_order_num) ? (
                          <span className="flex-shrink-0 text-xs" style={{ color: "#A3A3A3" }}>
                            {formatGoodsSelectionText(item.display_order_num)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center" style={{ color: "#CCCCCC" }}>
                      <i className="ri-arrow-right-s-line text-lg" />
                    </div>
                  </button>
                ))
              ) : (
                <div className="py-8 text-center text-sm" style={{ color: "#AAAAAA" }}>暂无可预约服务</div>
              )}
            </div>
          )}

          {activeTab === "reviews" && (
            <div className="px-4 py-3 space-y-4">
              <div className="flex items-center gap-4 rounded-[24px] px-4 py-5" style={{ background: "#FFF8F5" }}>
                <div className="flex min-w-[84px] flex-col items-center flex-shrink-0">
                  <span className="text-3xl font-bold" style={{ color: "#FF5C00" }}>{reviewRating.toFixed(1)}</span>
                  <div className="flex items-center gap-0.5 mt-1">
                    {[1, 2, 3, 4, 5].map((i) => <i key={i} className={i <= Math.floor(reviewRating) ? "ri-star-fill" : "ri-star-line"} style={{ color: "#FFD700", fontSize: 12 }} />)}
                  </div>
                  <span className="text-xs mt-0.5" style={{ color: "#AAAAAA" }}>综合评分</span>
                </div>
                <div className="flex-1 space-y-2">
                  {reviewBreakdown.map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <span className="w-[72px] flex-shrink-0 text-[12px]" style={{ color: "#7A8796" }}>{item.label}</span>
                      <div className="flex-1 rounded-full overflow-hidden" style={{ background: "#F1E7E2", height: 8 }}>
                        <div style={{ width: `${item.value}%`, height: "100%", background: "linear-gradient(90deg, #F59B4C, #F36B1C)", borderRadius: 99 }} />
                      </div>
                      <span className="text-[12px] flex-shrink-0 font-medium" style={{ color: "#F36B1C" }}>{item.value}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {reviewComments.length ? reviewComments.map((comment, index) => (
                <div key={comment.id}>
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: "#F5F5F5" }}>
                        {comment.user?.avatar ? <img src={resolveImage(comment.user.avatar)} alt={comment.user?.nickname || "用户头像"} className="w-full h-full object-cover" /> : <i className="ri-user-line" style={{ color: "#CCC", fontSize: 16 }} />}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>{comment.user?.nickname || "匿名用户"}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          {Array.from({ length: 5 }).map((_, i) => <i key={i} className={i < Math.round(toNumber(comment.service_comment, 5)) ? "ri-star-fill" : "ri-star-line"} style={{ color: "#FFD700", fontSize: 11 }} />)}
                        </div>
                      </div>
                    </div>
                    <span className="pt-1 text-xs" style={{ color: "#CCCCCC" }}>{formatDateText(comment.create_time)}</span>
                  </div>
                  <p className="mb-3 pl-11 text-sm leading-[1.9]" style={{ color: "#4A5568" }}>{comment.comment || "用户未填写评价内容"}</p>
                  {(comment.tags || []).length ? (
                    <div className="flex flex-wrap gap-2 pl-11">
                    {(comment.tags || []).map((tag) => (
                      <span key={tag} className="rounded-full px-3 py-1 text-xs" style={{ background: "#FFF3EC", color: "#FF9144" }}>
                        {tag}
                      </span>
                    ))}
                    </div>
                  ) : null}
                  {index < reviewComments.length - 1 && <div className="mt-5" style={{ borderBottom: "1px solid #F5F5F5" }} />}
                </div>
              )) : (
                <div className="py-8 text-center text-sm" style={{ color: "#AAAAAA" }}>暂无评价</div>
              )}
            </div>
          )}
          {activeTab === "moments" && hasMoments ? (
            <div className="space-y-3 px-4 py-3" style={{ background: "#FAFAFA" }}>
              {moments.map((moment) => (
                <CoachMomentCard
                  key={moment.id}
                  moment={moment}
                  coachName={detail?.name || "技师"}
                  coachAvatar={resolveImage(detail?.work_photo || detail?.photos?.[0])}
                  commentValue={momentCommentInputs[moment.id] || ""}
                  submitting={submittingMomentCommentId === moment.id}
                  liking={submittingMomentLikeId === moment.id}
                  deletingCommentId={deletingMomentCommentId}
                  currentUserId={currentUserId}
                  commentsExpanded={Boolean(expandedMomentComments[moment.id])}
                  onToggleLike={() => void toggleMomentLike(moment.id)}
                  onToggleComments={() => toggleMomentComments(moment.id)}
                  onDeleteComment={(commentId) => requestDeleteMomentComment(moment.id, commentId)}
                  onCommentChange={(value) => updateMomentCommentInput(moment.id, value)}
                  onFocusComment={focusMomentComment}
                  onSubmitComment={() => void submitMomentComment(moment.id)}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="fixed bottom-0 bg-white w-full flex items-center gap-3 px-4 py-3" style={{ maxWidth: 480, left: "50%", transform: "translateX(-50%)", borderTop: "1px solid #F0F0F0", zIndex: 30 }}>
        <button type="button" onClick={() => void handleCollectToggle()} className="flex items-center justify-center gap-1.5 px-5 py-3 rounded-full cursor-pointer whitespace-nowrap flex-shrink-0" style={{ border: `1.5px solid ${collected ? "#FF5C00" : "#DDDDDD"}`, color: collected ? "#FF5C00" : "#888", background: collected ? "#FFF5EE" : "#fff" }}>
          <i className={collected ? "ri-heart-fill" : "ri-heart-line"} style={{ fontSize: 18 }} />
          <span className="text-sm font-medium">{collected ? "已收藏" : "收藏技师"}</span>
        </button>
        <button type="button" onClick={() => (detail?.goods_lists?.length ? openServicePicker() : navigate("/service"))} className="flex-1 py-3 rounded-full text-white font-bold text-sm cursor-pointer whitespace-nowrap" style={{ background: "linear-gradient(90deg, #FF8C3A, #FF5C00)" }}>
          预约理疗师
        </button>
      </div>

      {deleteMomentCommentConfirm ? (
        <div className="fixed inset-0 z-[95] flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.42)" }}>
          <div className="w-full rounded-3xl bg-white p-5 text-center shadow-2xl" style={{ maxWidth: 340 }}>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "#FFF2E8", color: "#FF5C00" }}>
              <i className="ri-delete-bin-6-line text-xl" />
            </div>
            <h3 className="text-base font-bold" style={{ color: "#1A1A1A" }}>删除这条评论？</h3>
            <p className="mt-2 text-xs leading-5" style={{ color: "#888" }}>删除后评论内容不可恢复，技师端也不会再展示这条评论。</p>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => setDeleteMomentCommentConfirm(null)} className="flex-1 rounded-full py-3 text-sm font-semibold" style={{ background: "#F7F7F7", color: "#666", border: "none" }}>取消</button>
              <button type="button" onClick={() => void deleteMomentComment(deleteMomentCommentConfirm.momentId, deleteMomentCommentConfirm.commentId)} className="flex-1 rounded-full py-3 text-sm font-semibold" style={{ background: "linear-gradient(90deg, #FF8C3A, #FF5C00)", color: "#fff", border: "none" }}>{deletingMomentCommentId === deleteMomentCommentConfirm.commentId ? "删除中" : "确认删除"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {imagePreview ? (
        <ImagePreviewModal
          title={imagePreview.title}
          images={imagePreview.images}
          initialIndex={imagePreview.initialIndex}
          onClose={() => setImagePreview(null)}
        />
      ) : null}
      {showServicePicker && detail?.goods_lists?.length ? (
        <ServicePickerModal goodsList={detail.goods_lists} quantities={serviceQuantities} onIncrease={increaseServiceQty} onDecrease={decreaseServiceQty} onClose={() => setShowServicePicker(false)} onSubmit={submitSelectedGoods} />
      ) : null}
      <CoachSwitchModal
        open={showCoachSwitchModal}
        loading={coachSwitchLoading}
        coaches={switchableCoaches}
        onClose={() => setShowCoachSwitchModal(false)}
        onSelect={handleSwitchCoach}
      />
        </>
      ) : (
        <div className="min-h-screen" />
      )}

      {showLoadingShell ? (
        <DetailLoadingShell
          previewImage={previewImage || FALLBACK_IMAGE}
          previewName={previewName}
          fadingOut={shellFadingOut}
          onBack={() => navigate(-1)}
        />
      ) : null}
    </div>
  );
}
